import { defineStore } from 'pinia';
import { ref } from 'vue';

import { ADMIN_SYSTEM_ID } from '../config/constants';
import type { LoginMutation } from '../graphql/generated/types';

const CODES_KEY = 'permissionCodes';
const ALL_PERMS_KEY = 'allWorkspacePermissions';
export const FULL_ACCESS_HOTFIX_KEY = 'fullAccessHotfix';

export const hasFullAccessHotfix = (): boolean =>
  sessionStorage.getItem(FULL_ACCESS_HOTFIX_KEY) === 'true';

type LoginWorkspace = LoginMutation['login'][number];

/**
 * ALL_PERMS_KEY 格式：{ [workspaceID]: { [systemID]: string[] } }
 * CODES_KEY 格式：string[]（當前 workspace 的 ADMIN_SYSTEM_ID codes）
 */
export const usePermissionStore = defineStore('permission', () => {
  const permissionCodes = ref<Set<string>>(
    new Set(JSON.parse(sessionStorage.getItem(CODES_KEY) || '[]')),
  );
  const fullAccess = ref(hasFullAccessHotfix());

  /**
   * 登入後儲存所有 workspace 的 permissions（以 workspaceID + systemID 為 key）
   * 在 ChooseHeadquarter 選擇 workspace 前呼叫
   */
  const saveLoginPermissions = (loginData: LoginWorkspace[]) => {
    const allPerms: Record<string, Record<string, string[]>> = {};
    for (const ws of loginData) {
      const bySystem: Record<string, string[]> = {};
      for (const perm of ws.permissions) {
        bySystem[perm.systemID] = (bySystem[perm.systemID] ?? []).concat(
          perm.code,
        );
      }
      allPerms[ws.workspaceID] = bySystem;
    }
    sessionStorage.setItem(ALL_PERMS_KEY, JSON.stringify(allPerms));
  };

  /**
   * 選擇 workspace 後，解析該 workspace 的 ADMIN_SYSTEM_ID permissions 並持久化
   * 在 setOrgSelection 之後呼叫
   */
  const resolveForWorkspace = (workspaceID: string) => {
    const allPerms: Record<string, Record<string, string[]>> = JSON.parse(
      sessionStorage.getItem(ALL_PERMS_KEY) || '{}',
    );
    const codes = allPerms[workspaceID]?.[ADMIN_SYSTEM_ID] ?? [];
    permissionCodes.value = new Set(codes);
    sessionStorage.setItem(CODES_KEY, JSON.stringify(codes));
  };

  const canViewPage = (permissionPrefix: string): boolean => {
    return (
      fullAccess.value ||
      permissionCodes.value.has(`${permissionPrefix}.*.Read`) ||
      permissionCodes.value.has(`${permissionPrefix}.*.Write`)
    );
  };

  const canWrite = (permissionPrefix: string): boolean => {
    return (
      fullAccess.value ||
      permissionCodes.value.has(`${permissionPrefix}.*.Write`)
    );
  };

  const setPermissionCodes = (codes: string[]) => {
    const uniqueCodes = [...new Set(codes)];
    permissionCodes.value = new Set(uniqueCodes);
    sessionStorage.setItem(CODES_KEY, JSON.stringify(uniqueCodes));
  };

  const enableFullAccess = () => {
    fullAccess.value = true;
    sessionStorage.setItem(FULL_ACCESS_HOTFIX_KEY, 'true');
  };

  const clearPermissions = () => {
    permissionCodes.value = new Set();
    fullAccess.value = false;
    sessionStorage.removeItem(CODES_KEY);
    sessionStorage.removeItem(ALL_PERMS_KEY);
    sessionStorage.removeItem(FULL_ACCESS_HOTFIX_KEY);
    localStorage.removeItem(CODES_KEY);
    localStorage.removeItem(ALL_PERMS_KEY);
  };

  return {
    permissionCodes,
    saveLoginPermissions,
    resolveForWorkspace,
    canViewPage,
    canWrite,
    setPermissionCodes,
    enableFullAccess,
    clearPermissions,
  };
});
